export interface CameraOptions {
  width?: number
  height?: number
  interval?: number
  onCapture?: (imageData: string) => void
}

export interface CameraState {
  isActive: boolean
  isCapturing: boolean
  lastCaptureTime: number | null
}

export interface CameraInstance {
  getState: () => CameraState
  startCamera: () => Promise<boolean>
  stopCamera: () => void
  capture: () => Promise<string | null>
  startAutoCapture: () => void
  stopAutoCapture: () => void
}

export function createMediaRecorderCamera({
  width = 320,
  height = 240,
  interval = 60,
  onCapture
}: CameraOptions = {}): CameraInstance {
  let stream: MediaStream | null = null
  let videoElement: HTMLVideoElement | null = null
  let canvasElement: HTMLCanvasElement | null = null
  let captureInterval: ReturnType<typeof setInterval> | null = null
  let isActive = false
  let isCapturing = false
  let lastCaptureTime: number | null = null

  const captureFrame = async (): Promise<string | null> => {
    if (!videoElement || !canvasElement || !stream || !isCapturing) {
      return null
    }

    if (videoElement.readyState < 2) {
      return null
    }

    try {
      const ctx = canvasElement.getContext('2d')
      if (!ctx) {
        return null
      }

      canvasElement.width = videoElement.videoWidth
      canvasElement.height = videoElement.videoHeight

      ctx.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height)

      const quality = 0.8
      const dataUrl = canvasElement.toDataURL('image/jpeg', quality)
      lastCaptureTime = Date.now()

      return dataUrl
    } catch (err) {
      console.error('Frame capture error:', err)
      return null
    }
  }

  const startCamera = async (): Promise<boolean> => {
    try {
      if (stream) {
        return true
      }

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: width },
          height: { ideal: height },
          facingMode: 'user'
        },
        audio: false
      })

      stream = newStream

      videoElement = document.createElement('video')
      videoElement.autoplay = true
      videoElement.muted = true
      videoElement.playsInline = true
      videoElement.srcObject = stream

      await new Promise<void>((resolve, reject) => {
        videoElement!.onloadedmetadata = () => {
          videoElement!.play()
            .then(() => resolve())
            .catch(reject)
        }
        videoElement!.onerror = reject
        setTimeout(() => reject(new Error('Video load timeout')), 5000)
      })

      canvasElement = document.createElement('canvas')
      isActive = true

      return true
    } catch (err) {
      console.error('Camera access error:', err)
      return false
    }
  }

  const stopCamera = () => {
    if (captureInterval) {
      clearInterval(captureInterval)
      captureInterval = null
    }

    isCapturing = false

    if (videoElement) {
      videoElement.pause()
      videoElement.srcObject = null
      videoElement = null
    }

    if (canvasElement) {
      canvasElement = null
    }

    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      stream = null
    }

    isActive = false
  }

  const startAutoCapture = () => {
    if (!isActive || captureInterval) {
      return
    }

    isCapturing = true
    lastCaptureTime = null

    captureInterval = setInterval(async () => {
      if (!isCapturing) return

      const imageData = await captureFrame()
      if (imageData) {
        onCapture?.(imageData)
      }
    }, interval * 1000)
  }

  const stopAutoCapture = () => {
    isCapturing = false

    if (captureInterval) {
      clearInterval(captureInterval)
      captureInterval = null
    }
  }

  const capture = async (): Promise<string | null> => {
    if (!isActive) {
      return null
    }

    return await captureFrame()
  }

  const getState = (): CameraState => ({
    isActive,
    isCapturing,
    lastCaptureTime
  })

  return {
    getState,
    startCamera,
    stopCamera,
    capture,
    startAutoCapture,
    stopAutoCapture
  }
}
